<template>
  <ProductCardLayout
    :product
    :format="options.format"
    :comparison
    @toggle-comparison-product="emit('toggle-comparison-product')"
  >
    <template #tag>
      <!-- Tag space reservation (SR-5923 / SR-6346):
            - self has-…:mb-8 keeps the 8px under the tag on standalone cards
            - group-has-…/rail:min-h-[1lh] + …/rail:mb-8 make every card of a
              carousel rail reserve one tag line + 8px, but only when some card
              in the rail has a tag (group/rail sits on the carousel root)
            - data-marketing-tag only ever rides the v-if tag element, never
              this wrapper, so rails and grids without tags reserve 0px
            - withReservedTagSpace (SR-6346) is a surface opt-in used by the
              search dropdown (not a carousel): it reserves the same one line
              + 8px unconditionally, so suggestion cards align whether or not
              a card carries a tag. min-h-16 = 1lh of caption-bold, so the
              two policies can never double up -->
      <div
        class="flex caption-bold has-[[data-marketing-tag]]:mb-8 group-has-[[data-marketing-tag]]/rail:min-h-[1lh] group-has-[[data-marketing-tag]]/rail:mb-8"
        :class="{
          'mb-8 min-h-16': withReservedTagSpace,
          [$style.marketingTag]: options.format !== 'compact',
        }"
        data-spec="tag-slot"
      >
        <MarketingTag
          v-if="withMarketingTags && product.marketingTags?.[0]"
          :tag="product.marketingTags[0]"
        />
        <SalesLabel
          v-else-if="showSalesLabel"
          :sales-bucket="product.rawProduct.salesBucket"
        />
      </div>
    </template>
    <template #titles-first-row>
      <ProductCardDeal v-if="withDeal" class="relative mb-6" :deal-tag />
    </template>
    <template #titles-second-row>
      <RevButtonBase
        ref="link"
        class="after:focus-visible-outline-default-hi after:rounded-lg after:absolute after:inset-0 after:bg-transparent focus-visible:outline-none"
        :to="product.url"
        @click="cardClickHandler"
      >
        <span
          class="body-1-bold line-clamp-2 text-static-default-hi"
          data-test="product-title"
        >
          {{ product.title }}
        </span>
        <span
          v-if="enrichedDescription"
          class="text-static-default-low body-2"
          :class="options.extendedDescription ? 'line-clamp-3' : 'line-clamp-2'"
          data-test="product-description"
        >
          {{ enrichedDescription }}
        </span>
      </RevButtonBase>
    </template>
    <template #titles-third-row>
      <span v-if="product.warranty && withWarranty" class="body-2">
        {{
          i18n(translations.productCardWarranty, {
            warranty: product.warranty,
          })
        }}
        {{ i18n(translations.months) }}
      </span>
    </template>
    <template #product-info>
      <ProductCardRatings
        v-if="product.reviewRating?.count && product.reviewRating?.average"
        :count="product.reviewRating.count"
        data-spec="rating"
        :score="product.reviewRating.average"
      />
      <div v-if="product.specifications && product.specifications.length > 0">
        <ProductSpecs :specifications="product.specifications" />
      </div>
      <div class="flex w-full items-start justify-between gap-x-6">
        <div v-if="product.price" data-spec="price-information">
          <div
            v-if="product.productType === 'Brand new'"
            class="text-static-default-low caption"
            data-test="product-brand-new"
          >
            <RevTag :label="i18n(translations.brandNewLabel)" variant="info" />
          </div>
          <div
            v-else-if="options.withStartingFrom"
            class="text-static-default-low caption"
            data-test="product-starting-from"
          >
            {{ i18n(translations.startingFrom) }}
          </div>
          <ProductPrice
            :after-tradein-price="product.discountedPrice"
            :deal="withDeal ? eligibleDeal : undefined"
            :new-price="product.priceNew"
            :price="product.price"
          />
        </div>
        <AddToCartButton
          v-if="shouldShowAddToCart && options.format === 'compact'"
          :id="product.listingPublicId"
          :format="options.format"
          :product
          :trackingData="
            productToTrackingData(product.rawProduct, {
              ...options.tracking,
              withDeal,
              excludedDealTypes,
              position: options.position,
            })
          "
          zone="product_block_primary"
          @add-to-cart="addToCart"
        />
      </div>
    </template>
    <template #image>
      <RevIllustration
        alt=""
        data-test="product-card-image"
        :height="imageSize"
        :sizes="`${imageSize}px`"
        :src="product.imageSrc"
        :width="imageSize"
      />

      <ColorSwatches
        v-if="product.colorSwatches"
        :colors="product.colorSwatches"
        data-test="product-color-swatches"
      />
    </template>
    <template #footer>
      <ProductCardPaymentAdvertisement
        :base-price="product.price"
        class="mx-16 mb-16 min-h-40 md:min-h-68"
        :with-payment-advertisement
      />
      <div
        v-if="shouldShowAddToCart && options.format !== 'compact'"
        class="p-16 pt-0"
      >
        <AddToCartButton
          :id="product.listingPublicId"
          :format="options.format"
          :product
          :text="i18n(translations.addToCart)"
          :trackingData="
            productToTrackingData(product.rawProduct, {
              ...options.tracking,
              withDeal,
              excludedDealTypes,
              position: options.position,
            })
          "
          zone="product_block_primary"
          @add-to-cart="addToCart"
        />
      </div>
    </template>
  </ProductCardLayout>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import ProductCardPaymentAdvertisement from '@backmarket/nuxt-layer-payment/advertising/components/ProductCardPaymentAdvertisement/ProductCardPaymentAdvertisement.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'
import { RevTag } from '@ds/components/Tag'

import { useProductCardDeal } from '../../composables/useProductCardDeal'
import {
  getReferrerRoute,
  useProductClickStore,
} from '../../composables/useProductClickStore'
import {
  type ExtraTrackingData,
  productToTrackingData,
} from '../../composables/useProductTracking'
import { useSoldCountBadge } from '../../composables/useSoldCountBadge'
import AddToCartButton from '../AddToCartButton/AddToCartButton.vue'
import ColorSwatches from '../ColorSwatches.vue'
import ProductCardDeal from '../ProductCardDeal.vue'
import ProductCardRatings from '../ProductCardRatings.vue'
import ProductPrice from '../ProductPrice/ProductPrice.vue'
import ProductSpecs from '../ProductSpecs.vue'

import { BATTERY_SPECIAL_OFFER } from './ProductCard.constants'
import translations from './ProductCard.translations'
import type { Comparison, ProductCardProduct } from './ProductCard.types'
import ProductCardLayout from './ProductCardLayout.vue'
import MarketingTag from './components/MarketingTag.vue'
import SalesLabel from './components/SalesLabel.vue'

const {
  comparison,
  product,
  options = {},
} = defineProps<{
  product: ProductCardProduct
  options?: {
    withAddToCart?: boolean
    position?: number
    withStartingFrom?: boolean
    extendedDescription?: boolean
    format?: 'default' | 'compact'
    withMarketingTags?: boolean
    withDeal?: boolean
    excludedDealTypes?: Deal['type'][]
    withWarranty?: boolean
    withPaymentAdvertisement?: boolean
    forceHideAddToCart?: boolean
    withReservedTagSpace?: boolean
    tracking?: ExtraTrackingData
  }
  comparison?: Comparison
}>()

const {
  withMarketingTags = true,
  withDeal = true,
  excludedDealTypes = [],
  withWarranty = true,
  withPaymentAdvertisement = false,
  withReservedTagSpace = false,
} = options

const emit = defineEmits([
  'add-to-cart',
  'toggle-comparison-product',
  'link-click',
])

const i18n = useI18n()

const granularity = computed(() => product.rawProduct.granularity)

const { dealTag, eligibleDeal } = useProductCardDeal(
  product.deals,
  granularity.value,
  withDeal,
  excludedDealTypes,
)

const { isSoldCountBadgeEnabled } = useSoldCountBadge()

const hasMarketingTags = computed(
  () => (product.marketingTags?.length ?? 0) > 0,
)

// Parent card, bucket present, no marketing tag ("counter hides"), inside the
// rollout gate. No page or surface scoping. The Algolia-mapped nested product
// (rawProduct) is the source of truth for the badge fields.
const showSalesLabel = computed(
  () =>
    isSoldCountBadgeEnabled.value &&
    product.rawProduct.granularity === 'parent' &&
    product.rawProduct.salesBucket !== undefined &&
    !hasMarketingTags.value,
)

function addToCart({
  status,
  error,
}: {
  status: string | undefined
  error?: unknown
}) {
  emit('add-to-cart', { status, error })
}

const imageSize = computed(() => {
  if (options.format === 'compact') return 80

  return granularity.value === 'backbox' ? 224 : 128
})

const shouldShowAddToCart = computed(() => {
  if (options.forceHideAddToCart) return false

  return options.withAddToCart || granularity.value === 'backbox'
})

// TODO [SR-5653]: The backend will eventually return attributes as a proper array, which will
// remove the need for the ugly split(' • ') on the description string.
const enrichedDescription = computed(() => {
  if (granularity.value === 'parent') return undefined

  let backboxLabel: string | undefined
  if (granularity.value === 'backbox') {
    backboxLabel =
      product.productType === 'Brand new'
        ? i18n(translations.brandNewLabel)
        : product.grade?.name
  }

  return [
    backboxLabel,
    ...(product.description?.split(' • ') ?? []),
    granularity.value === 'backbox' &&
      product.specialOfferType === BATTERY_SPECIAL_OFFER &&
      i18n(translations.specialOfferType7),
  ]
    .filter(Boolean)
    .join(' • ')
})

const route = useRoute()
const storeProductCardData = useProductClickStore()
function cardClickHandler(event: MouseEvent) {
  storeProductCardData({
    listingId: product.listingPublicId ?? '',
    price: product.price.amount,
    referrerRoute: getReferrerRoute(route),
    category: product.rawProduct.category,
  })
  emit('link-click', event)
}
</script>

<style module>
@container product-card (width <= 326px) {
  .marketingTag:has([data-marketing-tag]) {
    min-height: 16px;
  }
}
</style>
